import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { pageWindow } from './pageWindow';

/** Numbered pager shared by the data grid and the detail-page card grids. */
export function PaginationBar({
  page,
  totalPage,
  onPageChange,
  isFetching,
  className,
}: {
  page: number;
  totalPage: number;
  onPageChange: (page: number) => void;
  isFetching?: boolean;
  className?: string;
}) {
  if (totalPage <= 1) return null;
  const atStart = page <= 1;
  const atEnd = page >= totalPage;

  return (
    <div
      className={cn('flex items-center justify-center gap-1 border-t p-2', className)}
      role="navigation"
      aria-label="Pagination"
    >
      <Button
        size="icon"
        variant="ghost"
        className="h-7 w-7"
        aria-label="First page"
        disabled={atStart}
        onClick={() => onPageChange(1)}
      >
        <ChevronsLeft className="h-4 w-4" />
      </Button>
      <Button
        size="icon"
        variant="ghost"
        className="h-7 w-7"
        aria-label="Previous page"
        disabled={atStart}
        onClick={() => onPageChange(page - 1)}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>

      {pageWindow(page, totalPage).map((p, i) =>
        p === null ? (
          <span key={`gap-${i}`} className="px-1 text-xs text-muted-foreground">
            …
          </span>
        ) : (
          <Button
            key={p}
            size="sm"
            variant={p === page ? 'default' : 'ghost'}
            className="h-7 min-w-7 px-2 text-xs tabular-nums"
            aria-current={p === page ? 'page' : undefined}
            onClick={() => onPageChange(p)}
          >
            {p}
          </Button>
        ),
      )}

      <Button
        size="icon"
        variant="ghost"
        className="h-7 w-7"
        aria-label="Next page"
        disabled={atEnd}
        onClick={() => onPageChange(page + 1)}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
      <Button
        size="icon"
        variant="ghost"
        className="h-7 w-7"
        aria-label="Last page"
        disabled={atEnd}
        onClick={() => onPageChange(totalPage)}
      >
        <ChevronsRight className="h-4 w-4" />
      </Button>

      {isFetching && <Loader2 className="ml-1 h-3.5 w-3.5 animate-spin text-muted-foreground" />}
    </div>
  );
}
